<template>
  <div>
    <PricingFilterToolbar
      :search-term="searchTerm"
      :expired-filter="expiredFilter"
      search-placeholder="Search (Description, Contract Term)..."
      @search-change="searchTerm = $event"
      @expired-filter-change="expiredFilter = $event"
    >
      <template v-slot:actions>
        <button v-if="userEditRight" type="button" @click=" pricePackage = deepCopyObject(emptyPackage); $refs.modifyPackageModal.open();" class="btn btn-info mr-3 mb-2">
          Add New Package
        </button>
      </template>
    </PricingFilterToolbar>
    <div class="table-responsive table-bordered" v-if="cappedPackages.length > 0 && packageSalesData.length > 0">
      <table class="table pricing-table" ref="table">
        <thead>
          <tr class="text-nowrap">
            <th class="table-font-size pricing-column-description">Description</th>
            <th class="table-font-size pricing-column-label">Label</th>
            <th class="table-font-size pricing-column-bandwidth">Download / Upload</th>
            <th class="table-font-size pricing-column-size">Size</th>
            <th class="table-font-size pricing-column-price">NRC (excl. VAT)</th>
            <th class="table-font-size pricing-column-price">MRC (excl. VAT)</th>
            <th class="table-font-size pricing-column-price">MRC (incl. VAT)</th>
            <th class="table-font-size pricing-column-term">Term</th>
            <th class="table-font-size pricing-column-status">Expired</th>
            <th class="table-font-size pricing-column-services">Services</th>
            <th class="table-font-size pricing-column-id">#</th>
            <th class="table-font-size pricing-column-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr :class="{ 'bg-primary': connectivityPackage.expired }" v-for="connectivityPackage in cappedPackages" :key="connectivityPackage.connectivityPackageNumber">
            <td>
              <strong>
                  <span class="font-size">{{ connectivityPackage.description }}</span>
              </strong>
            </td>
            <td class="pricing-column-label">
                  <span class="font-size">{{ connectivityPackage.label }}</span>
            </td>
            <td>
              <span class="font-size">
                {{ speedBitsToMbits(connectivityPackage.downloadSpeed)}} / {{ speedBitsToMbits(connectivityPackage.uploadSpeed) }} Mbps
              </span>
            </td>
            <td class="text-nowrap">
              <span class="font-size">{{ connectivityPackage.size }} GB</span>
            </td>
            <td class="text-nowrap">
              <span class="font-size">{{ getSetupFee(connectivityPackage.connectivitypackageprices, period) }}</span>
            </td>
            <td>
              <span class="font-size">{{ getPrice(connectivityPackage.connectivitypackageprices, period) }}</span>
              <span v-if="userEditRight" style="cursor: pointer" @click="openPriceModal(connectivityPackage)" v-tooltip="'Edit Row Price'" class="pull-right">
                <em class="fa fa-edit fa-fw text-green"></em>
              </span>
            </td>
            <td class="text-nowrap">
              <span class="font-size">{{ getPrice(connectivityPackage.connectivitypackageprices, period, true) }}</span>
            </td>
            <td class="text-nowrap">
              <span class="font-size">{{ connectivityPackage.contractTerm }}</span>
            </td>
            <td>
              <span class="badge" :class="connectivityPackage.expired ? 'badge-warning' : 'badge-green'">{{ connectivityPackage.expired }}</span>
            </td>
            <td class="text-nowrap text-center">
                <strong>
                  <span class="font-size">{{ servicesAmount(connectivityPackage) }}</span>
                </strong>
              </td>
            <td>
              <span class="font-size">{{ connectivityPackage.connectivityPackageNumber }}</span>
            </td>
            <td class="text-center">
              <button v-if="userEditRight" v-tooltip="'Edit Package'" @click="editPackage(connectivityPackage)" type="button" class="btn btn-sm btn-info m-1">
                <em class="fa fa-edit fa-fw"></em>
              </button>
              <button
                v-if="userEditRight" v-tooltip="'Delete Package'" @click="deletePackage(connectivityPackage)" type="button" class="btn btn-sm btn-primary">
                <em class="fa fa-trash fa-fw"></em>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="alert alert-info col-sm-12" v-else>
      <strong>No price packages to display</strong>
    </div>
    <ModifyCappedPackageModal
      ref="modifyPackageModal"
      :pricePackage="pricePackage"
      :shapedPools="shapedPools"
      :contractTerms="contractTerms"
      @packageAdded="packageAdded"
      @packageEdited="$emit('get-connectivity-packages')"
    />
    <AddPriceModal
      ref="addPriceModal"
      :row-price="rowPrice"
      :new-price="newPrice"
      :package-description="packageDescription"
      @addPrice="addPrice"
      @pricesAdded="$emit('get-connectivity-packages')"
    />
  </div>
</template>

<script>
import AddPriceModal from "@/components/Admin/Modals/AddPriceModal";
import ModifyCappedPackageModal from "@/components/Admin/Modals/ModifyCappedPackageModal";
import PricingFilterToolbar from "@/components/Admin/PricingFilterToolbar";
import PriceFunctions from "@/components/Admin/PriceFunctions";

export default {
  extends: PriceFunctions,
  props: {
    userEditRight: {
      type: Boolean,
      required: true
    },
    packages: {
      type: Array,
      required: true,
    },
    productNumber: {
      type: Number,
      required: true,
    },
    emptyPackage: {
      type: Object,
      required: true,
    },
  },
  components: {
    AddPriceModal,
    ModifyCappedPackageModal,
    PricingFilterToolbar,
  },
  data() {
    return {
      period: this.getCurrentPeriod(),
      pricePackage: {},
      newPrice: {},
      rowPrice: [],
      shapedPools: [],
      contractTerms: [],
      expiredFilter: 'false',
      filteredPackages: [],
      packageSalesData: [],
      packageSales: null,
      searchTerm: '',
      getPackageSaleAmounts: true,
      packageDescription: '',
    };
  },
  watch: {
    searchTerm(newVal, oldVal) {      
      if (oldVal !== newVal) {
        this.getPackageSaleAmounts = false;
      } 
    },
    expiredFilter(newVal, oldVal) {
      if (newVal !== oldVal && !this.getPackageSaleAmounts) {
        this.getPackageSaleAmounts = true;
      }
    }
  },
  computed: {
    cappedPackages() {     
      if (this.expiredFilter === 'true') {
        this.filteredPackages = this.packages.filter(p => p.expired === true);
      } else if (this.expiredFilter === 'both') {
        this.filteredPackages = this.packages;
      } else {
        this.filteredPackages = this.packages.filter(p => p.expired === false);
      }
      this.searchPackages(this.searchTerm); 
      this.getServiceAmounts(this.filteredPackages);
      return this.filteredPackages;
    },
  },
  mounted() {
    this.getShapedPools();
    this.getContractTerms();
  },
  methods: {
    getShapedPools: function() {
      const hostUrl = this.$config.aimsAPI;

      this.$http.get(`${hostUrl}shapedpools`).then(
        (response) => {
          this.shapedPools = response.data;
        },
        (error) => {
          this.showError("Error fetching Shaped Pools", error);
          console.error(error);
        }
      );
    },
    getContractTerms: function() {
      const hostUrl = this.$config.aimsAPI;

        this.$http.get(`${hostUrl}pricing/contractterms`).then(
        (response) => {
          this.contractTerms = response.data;
        },
        (error) => {
          this.showError("Error fetching Contract Terms", error);
          console.error(error);
        }
      );
    },
    getServiceAmounts: function(data) {     
      const hostUrl = this.$config.aimsAPI; 
      
      if (!this.searchTerm || this.getPackageSaleAmounts) {  
        this.$http.post(`${hostUrl}pricing/linkedservices/amount`, data).then(
          (response) => {
            this.packageSalesData = response.data;      
          },
          (error) => {
            this.showError("Error fetching package sales data", error);
            console.error(error);
          }
        );        
      }      
    },
    servicesAmount: function(packageData) {
      let saleAmount;        
      this.packageSalesData.filter(data => {
        if (data.connectivityPackageNumber === packageData.connectivityPackageNumber) {
          saleAmount = data.sales;
        }
      });   
      return saleAmount;
    },
    editPackage: function(connectivityPackage) {
      this.pricePackage = this.deepCopyObject(connectivityPackage);
      this.$refs.modifyPackageModal.open();
    },
    packageAdded: function(connectivityPackage) {
      connectivityPackage.connectivitypackageprices = [];
      connectivityPackage.shapedpool = this.shapedPools.find(
        (sp) => sp.shapedPoolNumber == connectivityPackage.shapedPoolNumber
      );
      this.packages.push(connectivityPackage);
      this.openPriceModal(connectivityPackage);
    },
    setNewPrice: function() {
      this.newPrice = this.deepCopyObject(
        this.emptyPackage.connectivitypackageprice
      );
    },
    openPriceModal: function(connectivityPackage) {
      this.setNewPrice();
      this.rowPrice = this.deepCopyObject(
        connectivityPackage.connectivitypackageprices
      );
      this.packageDescription = connectivityPackage.description || '';
      this.newPrice.connectivityPackageNumber =
        connectivityPackage.connectivityPackageNumber;
      this.$refs.addPriceModal.open();
    },
    addPrice: function(newPrice) {
      this.rowPrice.forEach((price) => {
        if (price.validUntil == 0) {
          if (newPrice.validFrom % 100 > 1) {
            price.validUntil = newPrice.validFrom - 1;
          } else {
            price.validUntil = newPrice.validFrom - 100 + 11;
          }
        }
      });
      this.rowPrice.push(newPrice);
      this.setNewPrice();
      this.newPrice.connectivityPackageNumber =
        newPrice.connectivityPackageNumber;
    },
    deletePackage: function(pricePackage) {
      this.$swal({
        title: "Are you sure you want to delete connectivity price package?",
        type: "warning",
        showCancelButton: true,
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          const hostUrl = this.$config.aimsAPI;

          this.$http.delete(`${hostUrl}pricing/pricepackages/${pricePackage.objKey}?objClassName=${pricePackage.class}`).then(
              () => {
                this.showSuccess("Successfully deleted price package");
                this.$emit("get-connectivity-packages");
              },
              (error) => {
                this.showError(
                  "Error Deleting Connectivity Pricing Package",
                  error
                );
              }
            );
        }
      });
    },
    speedBitsToMbits: function(value) {
      if (value && value > 0) {
        return value / 1000000;
      } else {
        return 0;
      }
    },
    searchPackages: function(searchTerm) {
      if (searchTerm) {
          this.filteredPackages = this.filteredPackages.filter(p => p.description.toLowerCase().includes(searchTerm.toLowerCase()) || p.contractTerm.toLowerCase().includes(searchTerm.toLowerCase()));
      }
    }
  },
};
</script>

<style scoped>
th.table-font-size {
  font-size: .95rem;
}

th.table-header-width-1 {
  width: 10px;
}

th.table-header-width-2 {
  width: 20px;
}

th.table-header-width-3 {
  width: 50px;
}

th.table-header-width-4 {
  width: 60px;
}

th.table-header-width-5 {
  width: 95px;
}

th.table-header-width-6 {
  width: 180px;
}

.font-size {
  font-size: .9rem;
}
</style>